import { KikUserZodSchemas as ZodSchemas } from "@kikocosmeticsorg/uc-api-nest-common-fe/zod";
import {
  AddressesAddressBookDto,
  AddressesAddressRequirementDto,
  SvcGetAddressRequirements,
  SvcGetCountrySubdivisions,
  SvcListAddressBook,
} from "@springtree/eva-services-core";
import { AddressesAddressDto } from "@springtree/eva-services-core/lib/eva-services-core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { z } from "zod";

import { useAuthContext } from "~/contexts/auth";
import { useEvaService } from "~/hooks/use-eva-service";
import Logger from "~/services/logger/logger";
import { MarketingCloudService } from "~/services/marketing-cloud.service";
import CommonUtils from "~/utils/common-utils";
import crossCountryUtils from "~/utils/cross-country-utils";

type ExtraValidationOriginalSchema = z.ZodString | z.ZodEffects<z.ZodString>;

export function useAddressRequirements(countryId: string) {
  const addressesListQueryKey = "getAddressesList";
  const getAddressRequirements = useEvaService(SvcGetAddressRequirements);
  const addressList = useEvaService(SvcListAddressBook);
  const getCountrySubdivisions = useEvaService(SvcGetCountrySubdivisions);
  const router = useRouter();
  const currentCountryID = crossCountryUtils.getCurrentCountryCode(router);
  const evaEndpointUrl = process.env.NEXT_PUBLIC_EVA_BACKEND_URL;
  const { isAuthenticated, user } = useAuthContext();
  const query = useQuery({
    queryKey: ["useAddressRequirements", countryId],
    staleTime: Infinity,
    cacheTime: Infinity,
    queryFn: async () => {
      return (await getAddressRequirements({ CountryID: `${countryId}` }))?.AddressRequirements ?? {};
    },
    enabled: !!evaEndpointUrl,
  });

  const hasSubdivisions =
    query.data &&
    "CountrySubdivisionID" in query.data &&
    (query.data.CountrySubdivisionID.Visible === 1 || query.data.CountrySubdivisionID.Visible === 3);

  const subdivisionsQuery = useQuery({
    queryKey: ["getCountrySubdivisions", countryId],
    staleTime: Infinity,
    cacheTime: Infinity,
    queryFn: async () => {
      return (await getCountrySubdivisions({ CountryID: `${countryId}` }))?.CountrySubdivisions;
    },
    enabled: !!evaEndpointUrl && query.isSuccess && hasSubdivisions,
  });

  const queryClient = useQueryClient();
  const [addressSchemaInfo, setAddressSchemaInfo] = useState<{
    source: AddressesAddressRequirementDto | void | {};
    schema: z.ZodType;
  }>({
    source: {},
    schema: z.object({}),
  });

  const specificErrorMessages: Record<string, Record<string, string>> = {
    PhoneNumber: {
      min: "error.valueTooShortIncludingPrefix",
      max: "error.valueTooLongIncludingPrefix",
    },
  };

  const earlyValidations: Record<string, <T extends string>(field: T, schema: z.ZodString) => z.ZodString> = {
    ZipCode<T>(field: T, schema: z.ZodString) {
      return schema.toUpperCase();
    },
  };

  const extraValidations: Record<
    string,
    <T extends string>(field: T, schema: ExtraValidationOriginalSchema) => z.ZodType | z.ZodEffects<z.ZodString>
  > = {
    EmailAddress<T>(field: T, schema: z.ZodString | z.ZodEffects<z.ZodString>) {
      return (schema as z.ZodString).email({
        message: "error.invalidEmail",
      });
    },
    PhoneNumber<T>(field: T, schema: z.ZodString | z.ZodEffects<z.ZodString>) {
      return ZodSchemas.phoneNumberSuperRefiner(schema);
    },
  };

  const errorMessageInvalidField: Record<string, string> = {
    ZipCode: "error.invalidFieldZipCode",
  };

  const generateSchemaFromRequirements = () => {
    let schema = z.object({});

    for (const [field, rules] of Object.entries(query.data || {})) {
      if (rules.Visible === 2) {
        // This field is hidden. Do not validate it.
        continue;
      }
      let validation: ExtraValidationOriginalSchema = z
        .string({
          invalid_type_error: "error.fieldRequired",
          required_error: "error.fieldRequired",
        })
        .trim();

      validation = earlyValidations[field]?.(field, validation) || validation;

      const minLength = rules.MinLength ?? 0;

      // apply min 1 for fields that are required and also have minLength > 1
      if (rules.Visible === 3 && minLength > 1) {
        validation = validation.min(1, { message: "error.fieldRequired" });
      }

      validation = validation
        .min(rules.Visible === 3 ? Math.max(minLength, 1) : minLength, {
          message:
            rules.Visible === 3 && minLength <= 1
              ? "error.fieldRequired"
              : specificErrorMessages[field]?.min || "error.valueTooShort",
        })
        .max(rules.MaxLength || 999, {
          message: specificErrorMessages[field]?.max || "error.valueTooLong",
        });

      if (rules.Validation) {
        Logger.instance.debug(`Adding regex validation for field ${field}`, rules.Validation);
        const validationWithoutRegex = validation;

        validation = validationWithoutRegex.refine(
          (value) => {
            try {
              validationWithoutRegex.parse(value);
              return new RegExp(rules.Validation!).test(value);
            } catch {}
          },
          {
            message: errorMessageInvalidField[field] ?? "error.invalidField",
          }
        );
      } else if (field === "CountrySubdivisionID" && subdivisionsQuery.data) {
        validation = validation.refine((val) => subdivisionsQuery.data?.some((s) => s.ID === val), {
          message: "error.invalidField",
        });
      }

      const finalSchema = extraValidations[field]?.(field, validation) || validation;

      if (field)
        if (rules.Visible === 1) {
          // This field is visible but optional.
          schema = schema.extend({ [field]: z.optional(finalSchema) });
        } else if (rules.Visible === 3) {
          // This field is visible and required.
          schema = schema.extend({ [field]: finalSchema });
        } else {
          throw new Error(`Unexpected visibility status ${rules.Visible} for field ${field}`);
        }
    }

    if (CommonUtils.parseBoolean(process.env.NEXT_PUBLIC_ADDRESS_EMAIL_MARKETING_CLOUD_VALIDATION_ENABLED)) {
      Logger.instance.debug(`Whole schema will be refined with MC check`);
      return schema.superRefine(async (data: Partial<Record<keyof AddressesAddressDto, string>>, ctx) => {
        try {
          schema.parse(data);
          return await MarketingCloudService.validateEmail(data.EmailAddress!)
            .then(() => !0)
            .catch(() =>
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "error.marketing_cloud_invalid_email",
                path: ["EmailAddress"],
              })
            );
        } catch {
          Logger.instance.debug("Form already invalid, skipping refinement");
        }
      });
    }

    return schema;
  };

  if (!CommonUtils.isEqual(query.data, addressSchemaInfo.source)) {
    setAddressSchemaInfo({
      source: query.data,
      schema: generateSchemaFromRequirements(),
    });
  }

  const getAddressesList = useQuery<AddressesAddressBookDto[]>({
    queryKey: [addressesListQueryKey, user?.ID, currentCountryID],
    staleTime: Infinity,
    queryFn: () => {
      return addressList({
        PageConfig: {
          Filter: {
            UserID: user?.ID,
            CountryIDs: [currentCountryID],
          },
        },
      }).then((r) => r?.Result?.Page || ([] as AddressesAddressBookDto[]));
    },
    enabled: !!(evaEndpointUrl && isAuthenticated),
    refetchOnMount: "always",
  });

  useEffect(() => {
    setAddressSchemaInfo({
      source: query.data,
      schema: generateSchemaFromRequirements(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subdivisionsQuery.isSuccess]);

  const invalidateCache = () => {
    queryClient.invalidateQueries([addressesListQueryKey, user?.ID, currentCountryID]);
  };

  return {
    addressSchema: addressSchemaInfo.schema,
    requirements: query.data || {},
    subdivisions: subdivisionsQuery.data,
    getAddressesList: getAddressesList,
    invalidateCache: invalidateCache,
  };
}
